import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Input,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import api from "../../api";
import AnimatedMapSprite from "../Maps/logic/AnimimatedMapSprite";

// Function to get color based on Pokemon type
const getTypeColor = (type) => {
  const typeColors = {
    Normal: "#A8A878",
    Fire: "#F08030",
    Water: "#6890F0",
    Electric: "#F8D030",
    Grass: "#78C850",
    Ice: "#98D8D8",
    Fighting: "#C03028",
    Poison: "#A040A0",
    Ground: "#E0C068",
    Flying: "#A890F0",
    Psychic: "#F85888",
    Bug: "#A8B820",
    Rock: "#B8A038",
    Ghost: "#705898",
    Dragon: "#7038F8",
    Dark: "#705848",
    Steel: "#B8B8D0",
    Fairy: "#EE99AC",
  };
  
  return typeColors[type] || "#68A090"; // Default color if type not found
};

const Pokedex = () => {
  document.title = "Pokedex | Pokémon Radiance";

  // identifier can be an ID or a name
  const [identifier, setIdentifier] = useState("1");
  const [inputValue, setInputValue] = useState("1");
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  // Fetch whenever identifier changes
  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get(`/v1/pokedex/${encodeURIComponent(identifier)}`, { withCredentials: true })
      .then((res) => setData(res.data))
      .catch(() => setError("Can't find pokemon"))
      .finally(() => setLoading(false));
  }, [identifier]);

  const handleSearch = (e) => {
    e.preventDefault();
    const v = inputValue.trim();
    if (v) setIdentifier(v);
  };

  if (loading) return <p style={{ color: "#fff" }}>Loading…</p>;

  // Keep last successful data; show error but do not clear data
  const { pokemon: poke, normal, variants, next } = data;

  // build cards: normal first, then other variants
  const cards = [
    {
      variant:       poke.name,
      rarity:        normal.rarity,
      caught:        normal.caught,
      evolves_to:    poke.pokevol,
      evolves_to_id: poke.evolves_to_id || null,
      evolve_level:  poke.evolve,
    },
    ...variants.map((v) => ({
      variant:       v.variant,
      rarity:        v.rarity,
      caught:        v.caught,
      evolves_to:    v.evolve || null,
      evolves_to_id: v.evolves_to_id || null,
      evolve_level:  poke.evolve,
    })),
  ];

  return (
    <>
      <div
        className="page-content"
        style={{ background: "#1d222e", minHeight: "100vh", padding: "2rem" }}
      >
        <style>{`
          .pokedex-card {
            transition: transform 0.2s ease, box-shadow 0.2s ease;
            background: rgba(0, 0, 0, 0.3);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            overflow: hidden;
          }
          .pokedex-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 16px rgba(0,0,0,0.3);
            border-color: rgba(255, 193, 7, 0.3);
          }
          .pokedex-card-header {
            background: rgba(0, 0, 0, 0.2);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            padding: 12px 16px;
          }
          .pokedex-card-body {
            padding: 16px;
          }
          .pokedex-stat {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 6px;
            font-size: 0.85rem;
          }
          .pokedex-stat-label {
            font-weight: bold;
            min-width: 80px;
          }
          .pokedex-stat-value {
            background: rgba(255, 255, 255, 0.1);
            padding: 2px 8px;
            border-radius: 4px;
          }
          .pokedex-search-input {
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 6px;
            color: #fff;
            padding: 8px 12px;
          }
          .pokedex-search-input:focus {
            border-color: rgba(255, 193, 7, 0.5);
            outline: none;
            box-shadow: 0 0 0 2px rgba(255, 193, 7, 0.2);
          }
          .pokedex-button {
            background: rgba(74, 144, 226, 0.8);
            border: none;
            color: #fff;
            padding: 8px 16px;
            border-radius: 6px;
            cursor: pointer;
            font-weight: bold;
            transition: all 0.2s ease;
          }
          .pokedex-button:hover {
            background: rgba(74, 144, 226, 1);
          }
          .pokedex-next {
            display: flex;
            align-items: center;
            gap: 8px;
            background: rgba(255, 255, 255, 0.1);
            padding: 8px 12px;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.2s ease;
          }
          .pokedex-next:hover {
            background: rgba(255, 255, 255, 0.2);
          }
          .pokedex-type-badge {
            display: inline-block;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 0.8rem;
            font-weight: bold;
            margin-right: 4px;
            text-shadow: 0 1px 1px rgba(0,0,0,0.5);
          }
        `}</style>
        <Container fluid>
          {/* Show error but keep current display */}
          {error && (
            <p style={{ color: "red", textAlign: "center", marginBottom: "1rem" }}>
              {error}
            </p>
          )}
          <br/>
          <Breadcrumbs title="Pokedex" breadcrumbItem={poke.name} />

          {/* Search bar + Next */}
          <Row className="mb-4">
            <Col md="6">
              <Card className="pokedex-card">
                <div className="pokedex-card-header">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-2">
                    <img 
                          src={`/items/pokedex.png`} 
                          alt={"pokedex"} 
                          style={{ 
                            width: 24, 
                            height: 24, 
                           
                          }} 
                        />
                      <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>Pokédex Search</h3>
                    </div>
                  </div>
                </div>
                <CardBody className="d-flex align-items-center gap-3 flex-wrap">
                  <form onSubmit={handleSearch} style={{ display: "flex", gap: "0.5rem", flex: 1 }}>
                    <Input
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder="ID or Name"
                      className="pokedex-search-input"
                      style={{ flex: 1, minWidth: "120px" }}
                    />
                    <button className="pokedex-button" type="submit">Search</button>
                  </form>
                  {next && (
                    <div
                      className="pokedex-next"
                      onClick={() => { setIdentifier(next); setInputValue(next); }}
                    >
                      <AnimatedMapSprite pokeName={next} style={{ width: 48, height: 48 }} />
                      <div style={{ color: "#fff", fontWeight: 600 }}>Next: {next}</div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Cards grid */}
          <Row className="g-3">
            {cards.map(({ variant, rarity, caught, evolves_to, evolves_to_id, evolve_level }) => {
              const { total, male, female, ungendered } = rarity;
              const rarityText = `${total} (${male}M/${female}F/${ungendered}G)`;
              // Type colors will be displayed as badges

              return (
                <Col key={variant} xs="12" sm="6" md="4" lg="3">
                  <Card className="pokedex-card">
                    <div className="pokedex-card-header">
                      <div className="d-flex align-items-center justify-content-between">
                        <h5 style={{ margin: 0, fontWeight: 600, fontSize: '16px' }}>{variant}</h5>
                        <div>
                          {caught ? 
                            <span style={{color: "#4caf50", fontWeight: 'bold', fontSize: '14px'}}>✓ Caught</span> : 
                            <span style={{color: "#f44336", fontSize: '14px'}}>Not Caught</span>
                          }
                        </div>
                      </div>
                    </div>
                    <CardBody className="text-center pokedex-card-body">
                      <div style={{ position: 'relative', marginBottom: '12px' }}>
                        <img 
                          src={`/sprites/${variant}.png`} 
                          alt={variant} 
                          style={{ 
                            width: 128, 
                            height: 128, 
                            filter: caught ? 'none' : 'grayscale(0.7) brightness(0.7)',
                            transition: 'all 0.3s ease'
                          }} 
                        />
                        <div style={{ 
                          position: 'absolute', 
                          bottom: '-10px', 
                          left: '50%', 
                          transform: 'translateX(-50%)',
                          background: 'rgba(0,0,0,0.5)',
                          borderRadius: '12px',
                          padding: '4px 8px',
                          backdropFilter: 'blur(4px)'
                        }}>
                          <AnimatedMapSprite pokeName={variant} style={{ width: 48, height: 48 }} />
                        </div>
                      </div>
                      
                      <div style={{ marginTop: '20px', marginBottom: '12px' }}>
                        {poke.type1 && (
                          <span 
                            className="pokedex-type-badge" 
                            style={{ 
                              background: getTypeColor(poke.type1),
                            }}
                          >
                            {poke.type1}
                          </span>
                        )}
                        {poke.type2 && (
                          <span 
                            className="pokedex-type-badge" 
                            style={{ 
                              background: getTypeColor(poke.type2),
                            }}
                          >
                            {poke.type2}
                          </span>
                        )}
                      </div>
                      
                      <div style={{ 
                        fontSize: "0.85rem", 
                        color: "#ccc", 
                        textAlign: "center",
                        background: 'rgba(0,0,0,0.2)',
                        padding: '6px',
                        borderRadius: '6px',
                        marginBottom: '12px'
                      }}>
                        <div>Rarity: {rarityText}</div>
                      </div>
                      
                      <div style={{ 
                        background: 'rgba(0,0,0,0.2)', 
                        borderRadius: '8px', 
                        padding: '10px',
                        marginBottom: '12px'
                      }}>
                        <div className="pokedex-stat">
                          <div className="pokedex-stat-label">HP:</div>
                          <div className="pokedex-stat-value" style={{flex: 1}}>{poke.hp}</div>
                        </div>
                        <div className="pokedex-stat">
                          <div className="pokedex-stat-label">Attack:</div>
                          <div className="pokedex-stat-value" style={{flex: 1}}>{poke.att}</div>
                        </div>
                        <div className="pokedex-stat">
                          <div className="pokedex-stat-label">Defense:</div>
                          <div className="pokedex-stat-value" style={{flex: 1}}>{poke.def}</div>
                        </div>
                        <div className="pokedex-stat">
                          <div className="pokedex-stat-label">Speed:</div>
                          <div className="pokedex-stat-value" style={{flex: 1}}>{poke.speed}</div>
                        </div>
                        <div className="pokedex-stat">
                          <div className="pokedex-stat-label">Sp. Atk:</div>
                          <div className="pokedex-stat-value" style={{flex: 1}}>{poke.specatt}</div>
                        </div>
                        <div className="pokedex-stat">
                          <div className="pokedex-stat-label">Sp. Def:</div>
                          <div className="pokedex-stat-value" style={{flex: 1}}>{poke.specdef}</div>
                        </div>
                      </div>
                      
                      {evolves_to && (
                        <div 
                          style={{ 
                            cursor: "pointer",
                            background: 'rgba(74, 144, 226, 0.2)',
                            borderRadius: '8px',
                            padding: '10px',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={() => { setIdentifier(evolves_to_id || evolves_to); setInputValue(evolves_to_id || evolves_to); }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(74, 144, 226, 0.3)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(74, 144, 226, 0.2)'}
                        >
                          <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '0.9rem' }}>Evolves at Level {evolve_level}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <AnimatedMapSprite pokeName={evolves_to} style={{ width: 48, height: 48 }} />
                            <div style={{ color: "#fff" }}>{evolves_to}</div>
                          </div>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Container>
      </div>
    </>
  );
};

export default Pokedex;